/**
 * Submits quiz answers to Dynamics 365 Customer Insights - Journeys (CIJ) form capture
 * when a visitor clicks a CTA button on the outcome page.
 *
 * Builds a hidden native <form> populated from quiz responses / the clicked CTA label,
 * then drives it through the official d365mktformcapture SDK (serializeForm/submitForm)
 * so we stay byte-compatible with whatever internal payload format Dynamics expects.
 */
import type { AddressValue, CijIntegrationConfig, Question, ResponseValue } from '../types';

interface D365MktFormCapture {
  waitForElement: (selector: string) => Promise<HTMLFormElement>;
  serializeForm: (
    form: HTMLFormElement,
    mappings: Array<{ FormFieldName?: string; DataverseFieldName: string; DataverseFieldValue?: string | Array<{ FormValue: string; DataverseValue: string }> }>,
  ) => { FormFieldMappingResults: Array<{ FormFieldMappingStatus: number; Message?: string }>; SerializedForm: { build: () => unknown } };
  submitForm: (config: { FormId: string; FormApiUrl: string }, payload: unknown) => Promise<unknown>;
}

declare global {
  interface Window {
    d365mktformcapture?: D365MktFormCapture;
  }
}

let scriptLoadPromise: Promise<void> | null = null;

function isOtherAnswerResponse(value: ResponseValue | undefined): value is { answerId: string } {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value) && 'answerId' in value;
}

function isAddressValue(value: ResponseValue | undefined): value is {
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country?: string;
} {
  return Boolean(value)
    && typeof value === 'object'
    && !Array.isArray(value)
    && 'addressLine1' in value
    && 'city' in value
    && 'stateProvince' in value
    && 'postalCode' in value;
}

function isRecordResponse(
  value: ResponseValue | undefined,
): value is Record<string, string | number | boolean | AddressValue | null | undefined> {
  return Boolean(value)
    && typeof value === 'object'
    && !Array.isArray(value);
}

function getResponseCaptureValue(response: ResponseValue | undefined): string {
  if (Array.isArray(response)) return response.map(String).join(',');
  if (isOtherAnswerResponse(response)) return response.answerId;
  if (isAddressValue(response)) {
    return [
      response.addressLine1,
      response.addressLine2,
      response.city,
      response.stateProvince,
      response.postalCode,
      response.country,
    ]
      .map((part) => (part ?? '').trim())
      .filter(Boolean)
      .join(', ');
  }
  if (isRecordResponse(response)) {
    return Object.values(response)
      .map((value) => getResponseCaptureValue(value as ResponseValue))
      .map((value) => value.trim())
      .filter(Boolean)
      .join(', ');
  }
  return String(response ?? '');
}

function getChoiceCaptureValue(response: ResponseValue | undefined, question: Question): string {
  if (Array.isArray(response)) {
    return response.map((value) => getChoiceCaptureValue(value, question)).join(',');
  }
  if (isOtherAnswerResponse(response)) {
    const otherValue = (response as { otherValue?: string }).otherValue;
    if (otherValue?.trim()) return otherValue;
    return getChoiceCaptureValue(response.answerId, question);
  }
  return question.answers.find((answer) => String(answer.id) === String(response))?.text
    ?? getResponseCaptureValue(response);
}

function loadScript(url: string): Promise<void> {
  if (window.d365mktformcapture) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load form capture script')));
      return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load form capture script'));
    document.body.appendChild(script);
  });

  return scriptLoadPromise;
}

/**
 * @param responses Assessment responses keyed by question id (from the submitted quiz)
 * @param ctaLabel The label of the CTA button the visitor clicked
 */
export async function submitFormCapture(
  config: CijIntegrationConfig,
  responses: Record<number, ResponseValue> | undefined,
  ctaLabel: string,
  questions: Question[] = [],
  prospectEmail?: string | null,
): Promise<void> {
  if (!config.enabled || !config.scriptUrl || !config.formId || !config.formApiUrl) return;

  await loadScript(config.scriptUrl);
  if (!window.d365mktformcapture) throw new Error('Form capture script did not initialize');

  const formEl = document.createElement('form');
  formEl.id = `cla-form-capture-${Date.now()}`;
  formEl.style.display = 'none';
  // eslint-disable-next-line no-console
  console.log('[CIJ] Creating form capture form', { formId: config.formId, mappingCount: config.fieldMappings.length });

  const mappings: Array<{ FormFieldName?: string; DataverseFieldName: string; DataverseFieldValue?: string | Array<{ FormValue: string; DataverseValue: string }> }> = [];
  const stringResponses = (responses ?? {}) as Record<string, ResponseValue>;

  config.fieldMappings.forEach((sourceMapping, index) => {
    const mapping = { ...sourceMapping };
    // Older saves regenerated answer IDs without updating CIJ mappings. Recover
    // only unambiguous text destinations; never guess numeric Dataverse choices.
    if (mapping.source === 'question' && mapping.sourceFieldId === undefined && mapping.valueMap?.length) {
      const question = questions.find((item) => String(item.id) === mapping.questionId);
      if (question && (question.type === 'SingleSelect' || question.type === 'MultiSelect')) {
        const valueMap = mapping.valueMap;
        mapping.valueMap = valueMap.map((entry) => {
          if (question.answers.some((answer) => String(answer.id) === entry.formValue)) return entry;
          const destination = entry.dataverseValue.trim();
          if (destination !== '' && Number.isFinite(Number(destination))) return entry;
          const normalizeLabel = (label: string): string => label.trim().replace(/[.!?]+$/, '').trimEnd();
          const matches = question.answers.filter((answer) => normalizeLabel(answer.text) === normalizeLabel(entry.dataverseValue));
          if (matches.length !== 1) return entry;
          const recoveredId = String(matches[0].id);
          return valueMap.some((existing) => existing.formValue === recoveredId)
            ? entry
            : { ...entry, formValue: recoveredId };
        });
      }
    }
    // Fixed-value fields (e.g. campaignid) carry no form field at all
    if (mapping.source === 'static' && !mapping.valueMap) {
      mappings.push({ DataverseFieldName: mapping.dataverseFieldName, DataverseFieldValue: mapping.staticValue ?? '' });
      // eslint-disable-next-line no-console
      console.log('[CIJ] Added fixed field mapping', { dataverseFieldName: mapping.dataverseFieldName });
      return;
    }

    let rawValue = '';
    let responseKeys: string[] = [];
    let sourceFieldKey: string | undefined;
    if (mapping.source === 'ctaClick') {
      rawValue = ctaLabel;
    } else if (mapping.source === 'prospectEmail') {
      rawValue = prospectEmail ?? '';
    } else if (mapping.source === 'static') {
      rawValue = mapping.staticValue ?? '';
    } else if (mapping.source === 'pageUrl') {
      rawValue = window.location.href;
    } else if (mapping.source === 'question' && mapping.questionId && responses) {
      const response = stringResponses[mapping.questionId];
      const question = questions.find((item) => String(item.id) === mapping.questionId);
      responseKeys = response && typeof response === 'object' && !Array.isArray(response)
        ? Object.keys(response)
        : [];
      if (mapping.sourceFieldKey && isRecordResponse(response)) {
        const responseRecord = response as Record<string, ResponseValue | undefined>;
        const fallbackFieldKey = mapping.sourceFieldKey === 'auditorName'
          ? 'subject'
          : mapping.sourceFieldKey === 'subject'
            ? 'auditorName'
            : undefined;
        rawValue = getResponseCaptureValue(
          responseRecord[mapping.sourceFieldKey]
            ?? (fallbackFieldKey ? responseRecord[fallbackFieldKey] : undefined),
        );
      } else if (mapping.sourceFieldId !== undefined && isRecordResponse(response)) {
        sourceFieldKey = `field_${mapping.sourceFieldId}`;
        const responseRecord = response as Record<string, ResponseValue | undefined>;
        rawValue = getResponseCaptureValue(responseRecord[sourceFieldKey]);
      } else if (mapping.sourceFieldId === undefined && !mapping.valueMap?.length
        && question && (question.type === 'SingleSelect' || question.type === 'MultiSelect')) {
        rawValue = getChoiceCaptureValue(response, question);
      } else {
        rawValue = getResponseCaptureValue(response);
      }
    }

    const fieldName = `field_${index}`;
    // The SDK maps the complete input value, not each comma-separated selection.
    if (mapping.source === 'question' && mapping.sourceFieldId === undefined && mapping.valueMap?.length
      && Array.isArray(stringResponses[mapping.questionId ?? ''])) {
      const selected = stringResponses[mapping.questionId ?? ''] as ResponseValue[];
      const values = selected.map((value) => mapping.valueMap!.find((entry) => entry.formValue === getResponseCaptureValue(value))?.dataverseValue);
      if (selected.length > 1 && values.every((value) => value !== undefined)) {
        mapping.valueMap = [...mapping.valueMap, { formValue: rawValue, dataverseValue: values.join(',') }];
      }
    }
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = fieldName;
    input.value = rawValue;
    formEl.appendChild(input);

    // eslint-disable-next-line no-console
    console.log('[CIJ] Filled form field', {
      fieldName,
      dataverseFieldName: mapping.dataverseFieldName,
      source: mapping.source,
      sourceQuestionId: mapping.questionId,
      sourceFieldId: mapping.sourceFieldId,
      sourceFieldKey,
      responseKeys,
      rawValueLength: rawValue.length,
      mappedValueCount: mapping.valueMap?.length ?? 0,
    });

    mappings.push(
      mapping.valueMap?.length
        ? {
            FormFieldName: fieldName,
            DataverseFieldName: mapping.dataverseFieldName,
            DataverseFieldValue: mapping.valueMap.map((v) => ({ FormValue: v.formValue, DataverseValue: v.dataverseValue })),
          }
        : { FormFieldName: fieldName, DataverseFieldName: mapping.dataverseFieldName },
    );
  });

  document.body.appendChild(formEl);
  // eslint-disable-next-line no-console
  console.log('[CIJ] Form created and appended', { formId: formEl.id, fieldCount: formEl.elements.length });
  try {
    const form = await window.d365mktformcapture.waitForElement(`#${formEl.id}`);
    // eslint-disable-next-line no-console
    console.log('[CIJ] Form found by CIJ SDK', { formId: formEl.id });
    const serialized = window.d365mktformcapture.serializeForm(form, mappings);
    const errors = serialized.FormFieldMappingResults.filter((r) => r.FormFieldMappingStatus === 2);
    // eslint-disable-next-line no-console
    console.log('[CIJ] Form serialized', { mappingCount: mappings.length, mappingErrorCount: errors.length });
    if (errors.length) {
      // eslint-disable-next-line no-console
      console.warn('Form capture: some field mappings failed', errors);
    }
    const payload = serialized.SerializedForm.build();
    // eslint-disable-next-line no-console
    console.log('[CIJ] Submitting form', { formId: config.formId, formApiUrl: config.formApiUrl });
    await window.d365mktformcapture.submitForm({ FormId: config.formId, FormApiUrl: config.formApiUrl }, payload);
    // eslint-disable-next-line no-console
    console.log('[CIJ] Form submission completed', { formId: config.formId });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[CIJ] Form submission failed', error);
    throw error;
  } finally {
    formEl.remove();
    // eslint-disable-next-line no-console
    console.log('[CIJ] Form removed', { formId: formEl.id });
  }
}
